import React, { Component } from "react";
import PropTypes from "prop-types";
import { PaymentService } from "@services";
import { CARD_ICONS, BANK_ACCOUNT_STATUSES } from "@constants";
import utils from "@utils";
import {
	AddPaymentMethodModal,
	BankVerificationModal,
	Loader,
	Image,
	ConfirmationModal,
} from "../../../components";

class PaymentMethods extends Component {
	constructor(props) {
		super(props);

		this.state = {
			paymentMethods: [],
			ownerId: null,
			loading: false,
		};
	}

	componentDidMount() {
		this.getPaymentMethods();
	}

	/** Retrieves a list of the users payment methods */
	getPaymentMethods = () => {
		const { lang } = global;

		this.setState({ loading: true });

		PaymentService.getData()
			.then(({ data }) => {
				this.setState({
					paymentMethods: data ? utils.extractPaymentMethods(data) : [],
					ownerId: data ? data.UserId : null,
					loading: false,
				});
			})
			.catch(({ response }) => {
				if (response?.status !== 404) {
					this.setState({ loading: false });

					utils.notify({
						type: "error",
						message: response.data.Message || lang.t("Error loading Payment Methods"),
					});
				}
			});
	};

	/**
	 * Removes the selectd payment method form the list
	 * @param {Number} id The unique id of the payment method that will be deleted
	 */
	deletePaymentMethod = id => {
		const { lang } = global;

		this.setState({ loading: true });

		PaymentService.deletePaymentMethod(id)
			.then(() => {
				this.setState(state => ({
					paymentMethods: state.paymentMethods.filter(item => item.id !== id),
					loading: false,
				}));

				utils.notify({
					type: "success",
					message: "Payment method successfully deleted",
				});
			})
			.catch(() => {
				this.setState({ loading: false });

				utils.notify({
					type: "error",
					message: lang.t("Error deleting Payment Method"),
				});
			});
	};

	/**
	 * Sets the preffered status of a seleceted card
	 * @param {Number} cardId The unique id of the card which will be set to preffered
	 */
	setPreferredCard = cardId => () => {
		const { lang } = global;

		this.setState({ loading: true });

		PaymentService.setPreferredCard(cardId)
			.then(() => {
				this.setState(state => ({
					paymentMethods: state.paymentMethods.map(item =>
						item.id === cardId
							? { ...item, isDefault: true }
							: { ...item, isDefault: false },
					),
					loading: false,
				}));

				utils.notify({
					type: "success",
					message: lang.t("Default card updated successfully"),
				});
			})
			.catch(() => {
				this.setState({ loading: false });

				utils.notify({
					type: "error",
					message: lang.t("Error setting Preferred Card"),
				});
			});
	};

	/**
	 * Displays a confirmation modal allowing the user to confirm their action before deleting a payment method
	 * @param {Number} id The unique id of the payment method that will be deleted
	 */
	onDelete = id => () => {
		const { lang } = global;

		this._confirmationModal.openModal({
			title: lang.t("Confirm"),
			message: lang.t("Are you sure you want to remove this payment method_"),
			confirmButton: `${lang.t("Yes")}, ${lang.t("Remove")}`,
			onConfirm: () => this.deletePaymentMethod(id),
		});
	};

	showAddPaymentMethod = () => {
		this._addPaymentMethodModal.openModal();
	};

	showBankAccountVerification = id => () => {
		this._bankVerificationModal.openModal(id);
	};

	/**
	 * Displays a label of the status of the bank account
	 * @param {String} accStatus The status of the bank account
	 */
	getBankAccountLabel = accStatus => {
		const bankAccount = Object.entries(BANK_ACCOUNT_STATUSES).find(
			([, value]) => value.value === accStatus,
		);

		if (bankAccount) return bankAccount[1].label;
		return BANK_ACCOUNT_STATUSES.DEFAULT.label;
	};

	render() {
		const { lang } = global;
		const { user } = this.props;
		const { paymentMethods, loading, ownerId } = this.state;

		return (
			<div className="my-account-panel">
				<div className="my-account-panel-header payment-methods-header">
					<div>
						<h4 className="title">{lang.t("Payment Methods")}</h4>
						<h5 className="subtitle">
							{lang.t("ACH or Credit Cards for payment of RKPay invoices_")}
						</h5>
					</div>
					<button
						type="button"
						className="btn btn-link"
						id="my-account-add-payment-method-btn"
						onClick={this.showAddPaymentMethod}
					>
						{lang.t("Add Payment Method")}
					</button>
				</div>
				<div className="my-account-panel-body payment-methods-body">
					<Choose>
						<When condition={paymentMethods.length === 0}>
							<div className="row">
								<div className="col-xs-12 text-center">
									<p className="text-muted">
										{lang.t(
											"There are no payment methods associated with this account",
										)}
									</p>
								</div>
							</div>
						</When>
						<Otherwise>
							<For
								each="item"
								index="index"
								of={paymentMethods}
							>
								<div
									className="row"
									key={`payment_method_${item.id}`}
								>
									<div className="col-xs-12 col-sm-6 text-left payment-method-name">
										<Choose>
											<When condition={item.type === "bank_account"}>
												{`${item.bankName} ${lang.t("ending in")} ${item.accNumber}`}
											</When>
											<When condition={item.type === "card"}>
												<span className="text-capitalize">{`${item.cardType} ${lang.t("Card")} `}</span>
												<Image
													src={
														CARD_ICONS[item.cardType] ||
														CARD_ICONS.default
													}
												/>
												<span>{item.cardNumber}</span>
											</When>
										</Choose>
									</div>
									<If condition={user.Id === ownerId || user.isSuperAdmin()}>
										<div className="col-xs-7 col-sm-2 text-right">
											<button
												type="button"
												className="btn btn-link remove-payment-method-btn"
												id={`remove-payment-method-${item.id}`}
												onClick={this.onDelete(item.id)}
											>
												{lang.t("Remove")}
											</button>
										</div>
										<div className="col-xs-5 col-sm-4 text-right">
											<Choose>
												<When condition={item.type === "bank_account"}>
													<Choose>
														<When
															condition={
																item.accStatus ===
																BANK_ACCOUNT_STATUSES.NEW.value
															}
														>
															<button
																type="button"
																className="btn btn-link"
																onClick={this.showBankAccountVerification(
																	item.id,
																)}
															>
																{lang.t("Verify")}
															</button>
														</When>
														<Otherwise>
															<div>
																{this.getBankAccountLabel(
																	item.accStatus,
																)}
															</div>
														</Otherwise>
													</Choose>
												</When>
												<When condition={item.type === "card"}>
													<If condition={item.isDefault}>
														{lang.t("Default")}
													</If>
													<If condition={!item.isDefault}>
														<button
															type="button"
															className="btn btn-link"
															onClick={this.setPreferredCard(item.id)}
														>
															{lang.t("Set as preferred")}
														</button>
													</If>
												</When>
											</Choose>
										</div>
									</If>
								</div>
								<If condition={index !== paymentMethods.length - 1}>
									<div className="my-account-separator-dashed" />
								</If>
							</For>
						</Otherwise>
					</Choose>
					<Loader active={loading} />
				</div>
				<ConfirmationModal
					ref={ref => {
						this._confirmationModal = ref;
					}}
				/>

				{/* callback reloads the payment methods list because the API doesn't return the new payment method data */}
				<AddPaymentMethodModal
					user={user}
					callback={this.getPaymentMethods}
					ref={ref => {
						this._addPaymentMethodModal = ref;
					}}
				/>

				{/* callback reloads the payment methods list because the API doesn't return the updated bank status */}
				<BankVerificationModal
					ref={ref => {
						this._bankVerificationModal = ref;
					}}
					callback={this.getPaymentMethods}
				/>
			</div>
		);
	}
}

PaymentMethods.propTypes = {
	user: PropTypes.object,
};

PaymentMethods.defaultProps = {
	user: {},
};

export default PaymentMethods;
